import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { compressImageToDataURL } from "@/lib/image";
import { toast } from "sonner";
import { Copy, Camera, Upload, X, BellOff, Bell } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export default function MemberProfile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", bio: "", avatar_url: "",
    gender: "", date_of_birth: "",
    quiet_hours_start: "", quiet_hours_end: "",
  });
  const [busy, setBusy] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [quietEnabled, setQuietEnabled] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) {
      const qStart = user.quiet_hours_start || "";
      const qEnd = user.quiet_hours_end || "";
      setForm({
        first_name: user.first_name || "", last_name: user.last_name || "",
        email: user.email || "", phone: user.phone || "", bio: user.bio || "", avatar_url: user.avatar_url || "",
        gender: user.gender || "",
        date_of_birth: (user.date_of_birth || "").slice(0, 10),
        quiet_hours_start: qStart, quiet_hours_end: qEnd,
      });
      setQuietEnabled(!!(qStart && qEnd));
    }
  }, [user]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    // Reset the input so re-picking the same file still fires onChange.
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    if (f.type !== "image/png" && !f.name.toLowerCase().endsWith(".png")) { toast.error("Only PNG images are allowed."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10MB before compression"); return; }
    setAvatarBusy(true);
    try {
      // Compress to <=512px longest edge · JPEG q=0.85. Keeps the DB
      // lean and every page that renders the avatar loads fast.
      const dataUrl = await compressImageToDataURL(f, { maxDim: 512 });
      // Optimistic preview.
      set("avatar_url", dataUrl);
      // Persist immediately so the new photo shows up everywhere in the
      // app — sidebar, dashboard, admin lists — without waiting for the
      // separate "Save changes" button (the previous UX was confusing).
      await persistAvatar(dataUrl);
    } catch (err) {
      toast.error("Could not process this image");
      setAvatarBusy(false);
    }
  };

  const persistAvatar = async (avatar_url) => {
    setAvatarBusy(true);
    try {
      await api.put("/profile", { avatar_url });
      await refresh();
      toast.success(avatar_url ? "Photo updated" : "Photo removed");
    } catch (err) {
      // Roll back the optimistic preview so the UI reflects reality.
      set("avatar_url", user?.avatar_url || "");
      toast.error(err?.response?.data?.detail || "Photo save failed");
    } finally {
      setAvatarBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Strip empty optional fields so backend Pydantic validation passes for `gender`
      // (Literal-typed → empty string would 422) and so we don't overwrite a stored DOB with "".
      const payload = { ...form };
      if (!payload.gender) delete payload.gender;
      if (!payload.date_of_birth) delete payload.date_of_birth;
      await api.put("/profile", payload);
      await refresh();
      toast.success("Profile saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };
  const copyId = () => { navigator.clipboard.writeText(user?.member_id || ""); toast.success("Member ID copied"); };
  const clearAvatar = () => {
    // Also persist a blank so the avatar is truly removed everywhere,
    // not just visually cleared until the next Save.
    set("avatar_url", "");
    persistAvatar("");
  };

  return (
    <AppShell variant="member" title="Your profile">
      <div className="max-w-2xl mx-auto">

        <Card className="p-7 rounded-2xl">
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="first_name" className="text-sm font-medium">First name</Label>
              <Input id="first_name" data-testid="profile-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1.5 h-11 rounded-lg" />
            </div>
            <div>
              <Label htmlFor="last_name" className="text-sm font-medium">Last name</Label>
              <Input id="last_name" data-testid="profile-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1.5 h-11 rounded-lg" />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input id="email" data-testid="profile-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11 rounded-lg" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-sm font-medium">Phone</Label>
              <Input id="phone" data-testid="profile-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 h-11 rounded-lg" />
            </div>
            <div>
              <Label htmlFor="date_of_birth" className="text-sm font-medium">Date of birth</Label>
              <Input
                id="date_of_birth"
                data-testid="profile-dob"
                type="date"
                value={form.date_of_birth}
                onChange={(e) => set("date_of_birth", e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className="mt-1.5 h-11 rounded-lg"
              />
            </div>
            <div>
              <Label htmlFor="gender" className="text-sm font-medium">Gender</Label>
              <Select
                value={form.gender || ""}
                onValueChange={(v) => set("gender", v)}
              >
                <SelectTrigger id="gender" data-testid="profile-gender" className="mt-1.5 h-11 rounded-lg">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male" data-testid="profile-gender-male">Male</SelectItem>
                  <SelectItem value="female" data-testid="profile-gender-female">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="bio" className="text-sm font-medium">Bio</Label>
              <Textarea id="bio" data-testid="profile-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={4} className="mt-1.5 rounded-lg" />
            </div>

            {/* Quiet hours — notification mute window */}
            <div className="sm:col-span-2 rounded-xl border border-border p-5 space-y-4" data-testid="profile-quiet-hours">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <BellOff className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-semibold tracking-tight">Quiet hours</div>
                    <p className="text-xs text-muted-foreground mt-0.5">Mute push notifications during set times. Dashboard notifications still arrive.</p>
                  </div>
                </div>
                <Switch
                  id="quiet-toggle"
                  data-testid="profile-quiet-toggle"
                  checked={quietEnabled}
                  onCheckedChange={(v) => {
                    setQuietEnabled(v);
                    if (!v) {
                      set("quiet_hours_start", "");
                      set("quiet_hours_end", "");
                    } else if (!form.quiet_hours_start) {
                      set("quiet_hours_start", "22:00");
                      set("quiet_hours_end", "07:00");
                    }
                  }}
                />
              </div>
              {quietEnabled && (
                <div className="flex flex-wrap items-center gap-3 pl-7.5">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="quiet_start" className="text-xs text-muted-foreground whitespace-nowrap">From</Label>
                    <Input
                      id="quiet_start"
                      data-testid="profile-quiet-start"
                      type="time"
                      value={form.quiet_hours_start}
                      onChange={(e) => set("quiet_hours_start", e.target.value)}
                      className="h-9 w-[120px] rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="quiet_end" className="text-xs text-muted-foreground whitespace-nowrap">Until</Label>
                    <Input
                      id="quiet_end"
                      data-testid="profile-quiet-end"
                      type="time"
                      value={form.quiet_hours_end}
                      onChange={(e) => set("quiet_hours_end", e.target.value)}
                      className="h-9 w-[120px] rounded-lg text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="sm:col-span-2 flex justify-end">
              <Button type="submit" disabled={busy} className="h-11 px-6 btn-primary-modern" data-testid="profile-save">{busy ? "Saving…" : "Save changes"}</Button>
            </div>
          </form>
        </Card>
      </div>
    </AppShell>
  );
}
